import React, { useEffect, useRef, useState } from 'react';
import { Button, ButtonProps, useMediaQuery } from '@mui/material';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import FableButterflyIcon from './FableButterflyIcon';
import './FableCallbackScreen.css';

type CallbackState = 'loading' | 'success' | 'error';
type Props = React.PropsWithChildren<{
  title: string;
  description?: string;
  eyebrow?: string;
  state?: CallbackState;
}>;

const sparks = [
  [14, 28, 7, 0], [79, 21, 9, -3], [24, 70, 8, -5], [85, 66, 10, -2],
  [35, 84, 9, -6], [71, 81, 8, -1], [8, 49, 10, -4], [92, 43, 7, -2],
  [39, 15, 8, -4], [61, 91, 9, -7], [20, 89, 10, -3], [87, 13, 8, -6],
];

/** The app's butterfly reveal, reused for short handoffs without delaying them. */
export default function FableCallbackScreen({
  title, description, eyebrow = 'BACK TO FABLE', state = 'loading', children,
}: Props) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const animation = useRef<LottieRefCurrentProps>(null);
  const [animationData, setAnimationData] = useState<unknown>();
  const [animationFailed, setAnimationFailed] = useState(false);
  const loading = state === 'loading';
  const showFinalFrame = () => animation.current?.goToAndStop(143, true);
  useEffect(() => { if (!loading) showFinalFrame(); }, [loading]);
  useEffect(() => {
    if (reducedMotion || typeof fetch !== 'function') return;
    const controller = new AbortController();
    let active = true;
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    // Keep decorative artwork out of the sign-in bundle and critical path.
    void fetch('/images/startup/fable-startup.json', { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error('Animation unavailable'); return response.json(); })
      .then(data => { if (active) setAnimationData(data); })
      .catch(() => { if (active) setAnimationFailed(true); })
      .finally(() => window.clearTimeout(timeout));
    return () => { active = false; window.clearTimeout(timeout); controller.abort(); };
  }, [reducedMotion]);

  return (
    <main className="fable-callback" data-state={state}>
      <div className="fable-callback__sky" aria-hidden="true" />
      <div className="fable-callback__aurora fable-callback__aurora--violet" aria-hidden="true" />
      <div className="fable-callback__aurora fable-callback__aurora--gold" aria-hidden="true" />
      <div className="fable-callback__sparks" aria-hidden="true">
        {sparks.map(([x, y, duration, delay], index) => <i key={index}
          className={index % 3 === 0 ? 'fable-callback__spark fable-callback__spark--star' : 'fable-callback__spark'}
          style={{ left: `${x}%`, top: `${y}%`, animationDuration: `${duration}s`, animationDelay: `${delay}s` }} />)}
      </div>
      <a href="/" className="fable-callback__brand" aria-label="Fable home">
        <FableButterflyIcon className="fable-callback__brand-mark" /><span>fable</span>
      </a>

      <section className="fable-callback__content" aria-labelledby="fable-callback-title">
        <div className="fable-callback__butterfly" aria-hidden="true">
          <div className="fable-callback__halo" />
          {reducedMotion || animationFailed || (!animationData && !loading) ? <FableButterflyIcon className="fable-callback__still" /> : animationData ? <Lottie
            lottieRef={animation}
            animationData={animationData}
            assetsPath="/images/startup/"
            loop={false}
            autoplay={loading}
            onDOMLoaded={() => { if (!loading) showFinalFrame(); }}
            onDataFailed={() => setAnimationFailed(true)}
            rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
            className="fable-callback__animation"
          /> : <span className="fable-callback__opening-spark" />}
        </div>
        <div className="fable-callback__copy" role={loading ? 'status' : undefined} aria-live="polite">
          <p className="fable-callback__eyebrow">{eyebrow}</p>
          <h1 id="fable-callback-title">{title}</h1>
          {description && <p className="fable-callback__description" role={state === 'error' ? 'alert' : undefined}>{description}</p>}
          {loading && <div className="fable-callback__waiting" aria-hidden="true"><span /><span /><span /></div>}
        </div>
        {children && <div className="fable-callback__actions">{children}</div>}
      </section>
    </main>
  );
}

export function CallbackAction({ secondary = false, className = '', ...props }: ButtonProps & { secondary?: boolean }) {
  return <Button {...props} className={`fable-callback__action ${secondary ? 'fable-callback__action--secondary' : ''} ${className}`} disableElevation />;
}
